import { SettingOutlined, UpOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import {
  CUSTOMISE_COLUMNS_ICON_FONT_SIZE,
  CustomiseColumnsIconButtonWithTooltip,
  customiseColumnsIconButtonStyle,
} from 'components/Custom/customise-columns-icon-button';
import { Col, Row, Select, Table, TableProps, Typography } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { TableRowSelection } from 'antd/es/table/interface';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatDisplayInteger,
  formatPaginationSummaryText,
  getLocalizedPageSizeSelectOptions,
  localizeDigitsForLocale,
} from 'i18n/arabicDigits';
import { useDispatch } from 'react-redux';
import { turnDrawerOff, turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { getDataPageRequest } from 'store/vat/actions';
import styled from 'styled-components';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { ColumnCustomizationDrawer } from './ColumnCustomizationDrawer';

/** Shared class names for table pagination page-size Select (trigger + popup). */
export const SCOPE_TABLE_PAGE_SIZE_SELECT_CLASS =
  'scope-table-page-size-select';
export const SCOPE_TABLE_PAGE_SIZE_DROPDOWN_CLASS =
  'scope-table-page-size-dropdown';

export const scopeTablePageSizeSelectProps = {
  className: SCOPE_TABLE_PAGE_SIZE_SELECT_CLASS,
  classNames: { popup: { root: SCOPE_TABLE_PAGE_SIZE_DROPDOWN_CLASS } },
};

interface StyledTableProps extends TableProps<any> {
  rowheight?: number;
  fontSize?: number;
  headerFontSize?: number;
}

const StyledTable = styled(Table)<StyledTableProps>`
  &.reconciliation-reports-table .ant-table-thead > tr > th {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  &.reconciliation-reports-table .recon-generated-by-col.ant-table-cell {
    width: 80px !important;
    min-width: 80px !important;
    max-width: 80px !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
    overflow: hidden;
  }

  &.ant-table-wrapper table,
  table {
    border-collapse: separate;
    border-spacing: 0 5px;
  }

  .ant-table-thead {
    font-size: ${(props) => props.headerFontSize ?? 13}px;
    font-weight: 600;
    line-height: 24px;
    text-align: start;
  }

  &.ant-table-wrapper .ant-table-thead > tr > th,
  &.ant-table-wrapper .ant-table-thead > tr > td,
  &.ant-table-wrapper .ant-table-thead .ant-table-cell,
  .ant-table-header .ant-table-thead > tr > th,
  .ant-table-header .ant-table-thead .ant-table-cell {
    background: var(--color-surface-base, #ffffff) !important;
    background-color: var(--color-surface-base, #ffffff) !important;
    // font-family: var(--font-family, Inter, sans-serif);
    font-size: ${(props) => props.headerFontSize ?? 13}px;
    font-weight: 600 !important;
    color: var(--color-text-base, rgba(0, 0, 0, 0.88)) !important;
  }

  .ant-table-header {
    background: transparent !important;
    background-color: transparent !important;
  }

  /* Allow specific tables (like transactions) to right-align numeric headers */
  .ant-table-thead > tr > th.transactions-amount-column {
    text-align: end;
  }

  .ant-table-thead
    > tr
    > th.transactions-amount-column
    .ant-table-column-sorters {
    margin-inline-start: auto;
  }

  .ant-table-thead
    > tr
    > th.transactions-amount-column
    .ant-table-column-title {
    text-align: end;
    /* Reduce right padding just for numeric headers to tighten spacing */
    padding-inline-end: 2px;
  }

  .ant-table-thead > tr > th,
  .ant-table-thead > tr > td {
    border-bottom: none;
  }

  .ant-table-thead > tr > th::before {
    width: 0px !important;
    background-color: var(--color-surface-base, #ffffff) !important;
  }

  .ant-table-tbody > tr > td {
    height: ${(props) => (props.rowheight ? props.rowheight : '45')}px;
    padding: 4px 12px;
    border-bottom: none;
    vertical-align: middle;
  }

  .ant-table-tbody > tr > td,
  .ant-table-tbody > tr > .ant-table-cell {
    background-color: var(--color-surface, #f3f7f8) !important;
  }
  .ant-table-tbody > tr > td:first-child,
  .ant-table-tbody > tr > .ant-table-cell:first-child {
    border-end-start-radius: 10px;
    border-start-start-radius: 10px;
  }

  .ant-table-tbody > tr > td:last-child,
  .ant-table-tbody > tr > .ant-table-cell:last-child {
    border-start-end-radius: 10px;
    border-end-end-radius: 10px;
  }

  .ant-table-tbody .ant-table-row,
  .ant-table-tbody td.ant-table-column-sort,
  .ant-table-tbody .ant-table-cell.ant-table-column-sort {
    background-color: var(--color-surface, #f3f7f8) !important;
    margin-bottom: 5px;
  }

  .ant-table-row:hover * a,
  .ant-table-row:hover * span[title] {
    text-decoration: underline;
  }

  .ant-table-row > .ant-table-cell-row-hover {
    background-color: #e6eff2 !important;
    .testing-icon {
      display: block;
    }
  }

  .ant-table-row.ant-table-row-selected > td,
  .ant-table-row.ant-table-row-selected > .ant-table-cell {
    background-color: #e1f0ee !important;
  }

  .ant-table-row.ant-table-row-selected > .ant-table-cell-row-hover {
    background-color: #d5e3e2 !important;
  }

  html[data-theme='dark'] & .ant-table-tbody > tr > td,
  html[data-theme='dark'] & .ant-table-tbody > tr > .ant-table-cell,
  html[data-theme='dark'] & .ant-table-tbody .ant-table-row,
  html[data-theme='dark'] & .ant-table-tbody td.ant-table-column-sort,
  html[data-theme='dark']
    &
    .ant-table-tbody
    .ant-table-cell.ant-table-column-sort {
    background-color: var(--color-surface, #132122) !important;
    color: var(--color-text-base, #e3eaeb) !important;
  }

  html[data-theme='dark'] &.ant-table-wrapper .ant-table-thead > tr > th,
  html[data-theme='dark'] &.ant-table-wrapper .ant-table-thead > tr > td,
  html[data-theme='dark'] &.ant-table-wrapper .ant-table-thead .ant-table-cell,
  html[data-theme='dark'] & .ant-table-header .ant-table-thead > tr > th,
  html[data-theme='dark'] & .ant-table-header .ant-table-thead .ant-table-cell,
  html[data-theme='dark'] & .ant-table-thead > tr > th.ant-table-column-sort,
  html[data-theme='dark']
    &
    .ant-table-thead
    > tr
    > th.ant-table-column-has-sorters,
  html[data-theme='dark']
    &
    .ant-table-thead
    > tr
    > th.ant-table-column-has-sorters:hover {
    background: var(--color-surface-raised, #1a2b2c) !important;
    background-color: var(--color-surface-raised, #1a2b2c) !important;
    color: var(--color-text-base, #e3eaeb) !important;
  }

  html[data-theme='dark'] & .ant-table-thead .ant-table-column-title,
  html[data-theme='dark'] & .ant-table-thead .ant-table-column-sorters,
  html[data-theme='dark'] & .ant-table-thead .ant-table-column-sorter,
  html[data-theme='dark'] & .ant-table-thead .ant-table-column-sorter .anticon,
  html[data-theme='dark'] & .ant-table-thead .ant-table-filter-column .anticon {
    color: var(--color-text-base, #e3eaeb) !important;
  }

  // "Most Popular" badge sits in thead but needs dark text on cyan.
  html[data-theme='dark'] & .ant-table-thead .pricing-most-popular-label {
    color: var(--color-on-primary, #052325) !important;
  }

  html[data-theme='dark'] & .ant-table-row > .ant-table-cell-row-hover {
    background-color: #203739 !important;
  }

  html[data-theme='dark'] & .ant-table-row.ant-table-row-selected > td,
  html[data-theme='dark']
    &
    .ant-table-row.ant-table-row-selected
    > .ant-table-cell {
    background-color: #274547 !important;
  }

  html[data-theme='dark']
    &
    .ant-table-row.ant-table-row-selected
    > .ant-table-cell-row-hover {
    background-color: #305356 !important;
  }

  html[data-theme='dark'] & .ant-empty .ant-empty-description,
  html[data-theme='dark'] & .ant-empty-description {
    color: var(--color-text-muted, #a9b8b9) !important;
  }

  html[data-theme='dark'] & .ant-empty .ant-empty-image svg,
  html[data-theme='dark'] & .ant-empty-image svg {
    color: var(--color-text-muted, #a9b8b9) !important;
    fill: currentColor !important;
  }

  .testing-icon {
    display: none;
  }

  .ant-table-thead > tr > th.ant-table-column-sort,
  .ant-table-thead > tr > th.ant-table-column-has-sorters,
  .ant-table-thead > tr > th.ant-table-column-has-sorters:hover {
    background-color: var(--color-surface-base, #ffffff) !important;
  }

  .ant-table-column-title {
    padding-inline-end: 5px;
    z-index: 0 !important;
  }

  .ant-table-column-sorters {
    width: fit-content !important;
  }

  .ant-table-column-sorters > .ant-table-column-title,
  .ant-table-cell,
  .ant-space-item {
    width: max-content;
  }

  .ant-table-tbody .ant-table-cell {
    text-wrap: nowrap;
    font-size: ${(props) => (props.fontSize ? props.fontSize : '13')}px;
    font-weight: 400;
  }

  .ant-table-content {
    overflow-x: auto;
    scrollbar-width: thin;
    width: 100%;
  }

  .context-menu-items {
    //width: 173px;
    max-height: 133px;
    border-radius: 10px;
    background: var(--color-surface-raised, #ffffff);
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
    box-shadow: 10px 10px 10px 0px #0000000d;
    border: 1px solid var(--color-border, #e9f6f7);
    color: var(--color-text-base, var(--color-text-dark, #042a2a));

    .ant-btn-icon svg,
    svg {
      color: inherit;
    }

    svg path[fill]:not([fill='none']) {
      fill: currentColor;
    }

    svg path[stroke] {
      stroke: currentColor;
      fill: none;
    }
  }

  .ant-popover-inner {
    padding: 8px !important;
  }

  .ant-pagination-total-text {
    margin-top: 10px;
    flex: 1 0 auto;
    min-width: max-content;
    white-space: nowrap;
  }

  .ant-pagination {
    flex-wrap: wrap;
    row-gap: 8px;
  }

  .expired-due-date {
    color: #d13131;
  }

  .ant-switch-inner {
    background: var(--color-border, #dbeae3) !important;
  }

  .switch-checked {
    > div::before {
      background: var(--color-primary, #41aaaf) !important;
    }
  }

  .ant-table-title {
    padding: ${(props) =>
      props.className == 'recap-statement' ||
      props.className == 'no-title-padding'
        ? '0px 0px'
        : props.className == 'show-selected-items'
        ? '8px 10px'
        : '16px 0px'} !important;
  }

  .no-padding {
    padding: 0 !important;
    padding-inline-end: 12px !important;
  }

  .ant-checkbox-indeterminate span:after {
    width: 10px !important;
  }

  .ant-select-selector {
    border-radius: 10px !important;
  }

  .ant-checkbox-indeterminate span:after {
    width: 10px !important;
  }
  .ant-checkbox-wrapper-checked span:after {
    top: 45%;
    inset-inline-start: 20%;
  }

  input[type='number'].no-spinner::-webkit-outer-spin-button,
  input[type='number'].no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    margin: 0 !important;
    display: none !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  input[type='number'].no-spinner {
    -moz-appearance: textfield !important;
    appearance: textfield !important;
  }

  input[type='number'].no-spinner:hover::-webkit-outer-spin-button,
  input[type='number'].no-spinner:hover::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    display: none !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  input[type='number'].no-spinner:focus::-webkit-outer-spin-button,
  input[type='number'].no-spinner:focus::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    display: none !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  .ant-input[type='number'].no-spinner::-webkit-outer-spin-button,
  .ant-input[type='number'].no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    display: none !important;
    opacity: 0 !important;
  }

  .net-amount-filter-select {
    width: auto !important;
    min-width: 100px !important;
  }

  .net-amount-filter-select .ant-select-selector {
    width: auto !important;
    min-width: 100px !important;
  }
`;

interface ScopeTableProps {
  className?: string;
  tableDataSource?: any[];
  originalDataSource?: any[];
  columns?: any[];
  pagination: boolean;
  rowsPerPage: number;
  enableColumnsCustomization: boolean;
  renderRightComponent?: React.JSX.Element | undefined;
  /** Rendered below the action buttons row (Mass Update + Columns). */
  renderBelowActionButtons?: React.ReactNode;
  titleComponent?: React.JSX.Element | undefined;
  rowHeight: number;
  mainLocaleKey?: string;
  haveBorder?: boolean;
  size?: SizeType;
  rowSelection?: TableRowSelection<any>;
  showSizeChanger: boolean;
  paginationSummaryOverride?: string;
  getNewPageSize: (page: number) => void;
  scroll?: any;
  fontSize?: number;
  headerFontSize?: number;
  total?: number;
  /**
   * When set (custom/server pagination), syncs the pager to the page that was
   * actually loaded. Pass only after a successful fetch so optimistic clicks
   * are not overwritten mid-request.
   */
  page?: number;
  transactionType?: string;
  reportId?: string;
  customPagination?: boolean;
  journalType?: string;
  rowClassName?: (record: any, index: number) => string;
  onRow?: (
    record: any,
    index: number
  ) => React.HTMLAttributes<HTMLTableRowElement>;
  rowKey?: string;
  searchFilterValue?: string;
  showAdjustedTransactions?: boolean;
  summary?: (currentPageData: readonly any[]) => React.ReactNode;
  onColumnsChange?: (config: {
    visibleColumns: string[];
    lockedColumns: string[];
    columnOrder: string[];
  }) => void;
  visibleColumns?: string[];
  lockedColumns?: string[];
  columnOrder?: string[];
  alwaysFixedColumns?: string[];
  optionalColumnKeys?: string[];
  onPageChange?: (page: number, pageSize: number, filters?: object) => void;
  disableColumnsCustomization?: boolean;
  hideToolbarColumnsCustomizationButton?: boolean;
  columnsCustomizationDrawerTitle?: string;
  columnsCustomizationDrawerTopSection?: React.ReactNode;
  columnsCustomizationDrawerSaveLabel?: string;
  defaultSort?: [string, 'asc' | 'desc'];
  /** Committed filter state from external ScopeTableFilters (server-side tables). */
  externalFiltersState?: Record<string, any>;
}

const ScopeTableInner: React.FC<ScopeTableProps> = ({
  className,
  tableDataSource,
  columns,
  pagination,
  rowsPerPage,
  enableColumnsCustomization,
  originalDataSource: _originalDataSource,
  rowHeight,
  fontSize,
  headerFontSize,
  mainLocaleKey: _mainLocaleKey,
  renderRightComponent,
  renderBelowActionButtons,
  titleComponent,
  haveBorder: _haveBorder,
  size,
  showSizeChanger,
  paginationSummaryOverride,
  getNewPageSize,
  rowSelection,
  scroll,
  total,
  page: loadedPage,
  transactionType = '',
  reportId = '',
  customPagination,
  journalType,
  rowClassName,
  onRow,
  searchFilterValue,
  showAdjustedTransactions,
  rowKey,
  summary,
  onColumnsChange,
  visibleColumns,
  lockedColumns,
  columnOrder,
  alwaysFixedColumns = [],
  optionalColumnKeys,
  onPageChange,
  disableColumnsCustomization = false,
  hideToolbarColumnsCustomizationButton = false,
  columnsCustomizationDrawerTitle,
  columnsCustomizationDrawerTopSection,
  columnsCustomizationDrawerSaveLabel,
  defaultSort,
  externalFiltersState,
}) => {
  const { t, i18n } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const dispatch = useDispatch();

  // Memoize customizable columns for drawer (only recalculate when columns or alwaysFixedColumns change)
  const customizableColumnsForDrawer = React.useMemo(
    () =>
      columns
        ?.filter(
          (col) =>
            col.key && !col.fixed && !alwaysFixedColumns.includes(col.key)
        )
        .map((col) => ({
          key: col.key,
          title: col.title,
          fixed: col.fixed,
        })) || [],
    [columns, alwaysFixedColumns]
  );

  // Memoize default visible columns
  const defaultVisibleColumnsForDrawer = React.useMemo(
    () =>
      visibleColumns ||
      columns?.filter((col) => col.key && !col.fixed).map((col) => col.key) ||
      [],
    [visibleColumns, columns]
  );

  const [filtersState, setFiltersState] = useState<Record<string, any>>(
    defaultSort ? { sort: { value: defaultSort } } : {}
  );

  const sortState = filtersState?.sort?.value as
    | [string, 'asc' | 'desc']
    | undefined;

  const resolveFiltersForRequest = useCallback(
    (partial: Record<string, any> = {}) => {
      if (!externalFiltersState) {
        return partial;
      }
      return { ...externalFiltersState, ...partial };
    },
    [externalFiltersState]
  );

  const [dataSource, setDataSource] = useState<any>();
  const skipInitialAdjustedFetchRef = React.useRef(true);

  const [rowsPerPageLocal, setRowsPerPageLocal] = useState(
    rowsPerPage ? rowsPerPage : 10
  );

  /** Keyed by filter key (e.g. netAmountConverted, net, tax, gross). Used for number filter UI and value. */
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(rowsPerPage);
  const [lastPage, setLastPage] = useState(1);
  const journalTypeRef = React.useRef(journalType);
  journalTypeRef.current = journalType;
  const getGroupingType = React.useCallback(() => {
    const active = journalTypeRef.current;
    return active?.toUpperCase() === 'SUMMARISED' ? 'summarised' : 'detailed';
  }, []);

  // Filter and order columns based on preferences, and normalise sorting so
  // sortable headers behave consistently across all ScopeTable usages.
  const filteredColumns = React.useMemo(() => {
    if (!columns || columns.length === 0) {
      return columns;
    }

    const currentSortKey = sortState?.[0];
    const currentSortDir = sortState?.[1];

    // If column customisation is disabled or no preferences saved, just return
    // all columns but attach controlled sort metadata to sortable ones.
    if (
      !enableColumnsCustomization ||
      !visibleColumns ||
      visibleColumns.length === 0
    ) {
      return columns.map((col) => {
        if (!col || !col.sorter) return col;

        const isSorted =
          col.key &&
          currentSortKey &&
          String(col.key) === String(currentSortKey);
        const sortOrder =
          isSorted && currentSortDir
            ? currentSortDir === 'asc'
              ? 'ascend'
              : 'descend'
            : null;

        const base = {
          ...col,
          sortDirections: ['ascend', 'descend'] as const,
          sortOrder,
        };

        // If a custom sortIcon is defined on the column (e.g. transactions tab),
        // keep it but wrap it so unsorted columns still reserve the same space.
        if (col.sortIcon) {
          return {
            ...base,
            sortIcon: (props: any) => (
              <span
                style={{
                  display: 'inline-flex',
                  width: 16,
                  justifyContent: 'center',
                  visibility: props.sortOrder ? 'visible' : 'hidden',
                }}
              >
                {col.sortIcon(props)}
              </span>
            ),
          };
        }

        // Otherwise, use a simple up/down arrow in a fixed-width wrapper so
        // columns don't resize when sort state changes.
        return {
          ...base,
          sortIcon: ({ sortOrder: order }) => (
            <span
              style={{
                display: 'inline-flex',
                width: 16,
                justifyContent: 'center',
                visibility: order ? 'visible' : 'hidden',
              }}
            >
              {order === 'descend' ? (
                <UpOutlined
                  style={{
                    fontSize: 10,
                    color: 'var(--color-text-muted, #5a6a6a)',
                    transform: 'rotate(180deg)',
                  }}
                />
              ) : (
                <UpOutlined
                  style={{
                    fontSize: 10,
                    color: 'var(--color-text-muted, #5a6a6a)',
                  }}
                />
              )}
            </span>
          ),
        };
      });
    }

    // Create Sets for faster lookups (O(1) instead of O(n))
    const visibleSet = new Set(visibleColumns);
    const alwaysFixedSet = new Set(alwaysFixedColumns || []);
    const lockedSet = new Set(lockedColumns || []);

    // Separate columns by their fixed position (single pass - more efficient)
    const leftFixedColumns: any[] = [];
    const rightFixedColumns: any[] = [];
    const regularColumns: any[] = [];

    for (const col of columns) {
      if (col.fixed === 'left' || (!col.fixed && !col.key)) {
        leftFixedColumns.push(col);
      } else if (col.fixed === 'right') {
        rightFixedColumns.push(col);
      } else if (col.key) {
        regularColumns.push(col);
      }
    }

    // Filter visible columns, excluding always-fixed (they're handled separately)
    let visibleRegularColumns = regularColumns.filter(
      (col) => visibleSet.has(col.key) && !alwaysFixedSet.has(col.key)
    );

    // Apply custom order if provided (excluding always-fixed columns from order)
    if (columnOrder && columnOrder.length > 0) {
      const orderMap = new Map(columnOrder.map((key, index) => [key, index]));
      visibleRegularColumns.sort((a, b) => {
        const aIndex = orderMap.get(a.key) ?? Infinity;
        const bIndex = orderMap.get(b.key) ?? Infinity;
        return aIndex - bIndex;
      });
    }

    // Separate user-locked columns (they become fixed right but stay in order)
    const lockedVisibleColumns: any[] = [];
    const unlockedVisibleColumns: any[] = [];

    for (const col of visibleRegularColumns) {
      if (lockedSet.has(col.key)) {
        lockedVisibleColumns.push({ ...col, fixed: 'right' as const });
      } else {
        unlockedVisibleColumns.push(col);
      }
    }

    // Get always-fixed columns that are visible (Net, Tax, Actions)
    const alwaysFixedVisible = regularColumns.filter(
      (col) => alwaysFixedSet.has(col.key) && visibleSet.has(col.key)
    );

    // Return in order: left-fixed, unlocked regular, locked (as fixed right),
    // always-fixed (at end), right-fixed, and attach controlled sort metadata
    // to sortable columns so clicking headers is a strict asc/desc toggle.
    const ordered = [
      ...leftFixedColumns,
      ...unlockedVisibleColumns,
      ...lockedVisibleColumns,
      ...alwaysFixedVisible,
      ...rightFixedColumns,
    ];

    return ordered.map((col) => {
      if (!col || !col.sorter) return col;

      const isSorted =
        col.key && currentSortKey && String(col.key) === String(currentSortKey);
      const sortOrder =
        isSorted && currentSortDir
          ? currentSortDir === 'asc'
            ? 'ascend'
            : 'descend'
          : null;

      const base = {
        ...col,
        sortDirections: ['ascend', 'descend'] as const,
        sortOrder,
      };

      if (col.sortIcon) {
        return {
          ...base,
          sortIcon: (props: any) => (
            <span
              style={{
                display: 'inline-flex',
                width: 16,
                justifyContent: 'center',
                visibility: props.sortOrder ? 'visible' : 'hidden',
              }}
            >
              {col.sortIcon(props)}
            </span>
          ),
        };
      }

      return {
        ...base,
        sortIcon: ({ sortOrder: order }) => (
          <span
            style={{
              display: 'inline-flex',
              width: 16,
              justifyContent: 'center',
              visibility: order ? 'visible' : 'hidden',
            }}
          >
            {order === 'descend' ? (
              <UpOutlined
                style={{
                  fontSize: 10,
                  color: 'var(--color-text-muted, #5a6a6a)',
                  transform: 'rotate(180deg)',
                }}
              />
            ) : (
              <UpOutlined
                style={{
                  fontSize: 10,
                  color: 'var(--color-text-muted, #5a6a6a)',
                }}
              />
            )}
          </span>
        ),
      };
    });
  }, [
    columns,
    visibleColumns,
    lockedColumns,
    columnOrder,
    alwaysFixedColumns,
    enableColumnsCustomization,
    sortState,
  ]);

  const sendSearchQuery = (filters, reportId) => {
    fetchFilteredDataPage(filters, currentPageSize, reportId);
  };

  const debouncedSendQueryRef = React.useRef(debounce(sendSearchQuery, 500));

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSendQueryRef.current.cancel();
    };
  }, []);

  React.useEffect(() => {
    journalTypeRef.current = journalType;
    if (journalType && customPagination) {
      setCurrentPage(1);
      debouncedSendQueryRef.current.cancel();
    }
  }, [journalType, customPagination]);

  useEffect(() => {
    let source = tableDataSource;
    if (customPagination && source && source?.length > currentPageSize) {
      source = source?.slice(0, currentPageSize);
    }
    setDataSource(source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableDataSource, customPagination, currentPageSize]);

  useEffect(() => {
    if (rowsPerPage && rowsPerPage !== rowsPerPageLocal) {
      setRowsPerPageLocal(rowsPerPage);
    }
    if (
      typeof rowsPerPage === 'number' &&
      rowsPerPage > 0 &&
      rowsPerPage !== currentPageSize
    ) {
      setCurrentPageSize(rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage]);

  useEffect(() => {
    if (typeof loadedPage === 'number' && loadedPage >= 1) {
      setCurrentPage((prev) => (prev === loadedPage ? prev : loadedPage));
    }
  }, [loadedPage]);

  useEffect(() => {
    if (typeof total === 'number') {
      const size = currentPageSize > 0 ? currentPageSize : 1;
      const maxPage = Math.max(1, Math.ceil(total / size) || 1);
      setLastPage(maxPage);

      // Ensure currentPage never exceeds the new maxPage
      if (currentPage > maxPage) {
        setCurrentPage(maxPage);
      }
    }
  }, [currentPageSize, total, currentPage]);

  useEffect(() => {
    if (searchFilterValue || searchFilterValue === '') {
      const key = 'search';
      setFiltersState((prev) => {
        const currentFilter = { ...prev };
        delete currentFilter[key];
        if (searchFilterValue) {
          currentFilter[key] = { value: [searchFilterValue] };
        }
        debouncedSendQueryRef.current(currentFilter, reportId);
        return currentFilter;
      });
    }
  }, [searchFilterValue]);

  useEffect(() => {
    if (skipInitialAdjustedFetchRef.current) {
      skipInitialAdjustedFetchRef.current = false;
      return;
    }

    if (customPagination) {
      const key = 'type';
      const currentFiltersCopy = { ...filtersState };

      if (showAdjustedTransactions) {
        currentFiltersCopy[key] = {
          value: ['EXCEPTION'],
        };
      } else {
        delete currentFiltersCopy[key];
      }

      setFiltersState(currentFiltersCopy);
      fetchFilteredDataPage(currentFiltersCopy);
    }
  }, [showAdjustedTransactions]);

  const renderPaginationText = (_total, range) => {
    const tableTotal = total != undefined && total != null ? total : _total;

    return (
      <Typography
        style={{
          fontSize: '12px',
          fontWeight: '400',
          lineHeight: '16px',
          textAlign: 'start',
          color: 'var(--color-text-base, #042a2a)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {paginationSummaryOverride ??
          formatPaginationSummaryText(t, i18n.language, range, tableTotal)}
      </Typography>
    );
  };

  const pageSizeSelectOptions = useMemo(
    () => getLocalizedPageSizeSelectOptions(t, i18n.language),
    [t, i18n.language]
  );

  const pageSizeChangerProps = useMemo(
    () =>
      showSizeChanger
        ? {
            ...scopeTablePageSizeSelectProps,
            options: pageSizeSelectOptions,
            'aria-label': t('main.page-size-aria-label'),
          }
        : false,
    [showSizeChanger, pageSizeSelectOptions, t]
  );

  const handlePageClick = (type) => {
    const size = currentPageSize > 0 ? currentPageSize : 1;
    const computedLastPage =
      typeof total === 'number'
        ? Math.max(1, Math.ceil(total / size) || 1)
        : lastPage;

    let page = currentPage;
    if (type === 'first') {
      page = 1;
    } else if (type === 'prev') {
      page--;
    } else if (type === 'next') {
      page++;
    } else if (type === 'last') {
      page = computedLastPage;
    }

    if (page === currentPage || page < 1 || page > computedLastPage) {
      return;
    }

    setLastPage(computedLastPage);
    setCurrentPage(page);

    if (customPagination) {
      if (onPageChange) {
        onPageChange(
          page,
          currentPageSize,
          resolveFiltersForRequest(filtersState)
        );
      } else {
        dispatch(
          getDataPageRequest(
            reportId,
            page,
            currentPageSize,
            transactionType,
            getGroupingType(),
            resolveFiltersForRequest(filtersState)
          )
        );
      }
    }
  };

  const renderPaginationButtons = (_page, type, element) => {
    const size = currentPageSize > 0 ? currentPageSize : 1;
    const computedLastPage =
      typeof total === 'number'
        ? Math.max(1, Math.ceil(total / size) || 1)
        : lastPage;
    const isAtFirstPage = currentPage === 1;
    const isAtLastPage = currentPage >= computedLastPage;
    const isDisabled = customPagination
      ? type === 'first' || type === 'prev'
        ? isAtFirstPage
        : isAtLastPage
      : element?.props?.disabled;

    const paginationButtonStyle =
      type === 'first' || type === 'prev'
        ? { color: 'var(--color-text-muted, #5a6a6a)' }
        : undefined;

    if (
      type === 'first' ||
      type === 'prev' ||
      type === 'next' ||
      type === 'last'
    ) {
      const labelKey =
        type === 'first'
          ? 'main.first-page'
          : type === 'prev'
          ? 'main.previous'
          : type === 'next'
          ? 'main.next'
          : 'main.last-page';
      const width = pwaMobileNav
        ? 'auto'
        : type === 'first' || type === 'last'
        ? '90px'
        : type === 'prev'
        ? '88px'
        : '75px';
      const button = (
        <ScopeButton
          width={width}
          height={pwaMobileNav ? '32px' : '25px'}
          fontSize={pwaMobileNav ? '11px' : '12px'}
          lineheight={pwaMobileNav ? '12px' : '12px'}
          onClick={() => handlePageClick(type)}
          type="default"
          style={paginationButtonStyle}
          disabled={isDisabled}
        >
          {t(labelKey)}
        </ScopeButton>
      );
      if (customPagination) {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            {button}
            {!pwaMobileNav ? (
              <span
                style={{
                  marginInlineEnd: type === 'last' ? '16px' : '8px',
                }}
              />
            ) : null}
          </span>
        );
      }
      return button;
    }
    return null;
  };

  const pageSizeChange = (value) => {
    // For tables without custom (server-side) pagination, shrink the current
    // page locally when page size decreases to avoid an unnecessary refetch.
    if (
      !customPagination &&
      currentPageSize > value &&
      Array.isArray(dataSource)
    ) {
      const updatedDataSource = dataSource.slice(0, value);
      setDataSource(updatedDataSource);
    } else {
      // For server-side (custom) pagination, always notify the parent / saga
      // so the backend returns the correct number of rows for the new size.
      fetchFilteredDataPage(filtersState, value);
    }

    getNewPageSize(value);
    setCurrentPageSize(value);
  };

  const renderPageSizeSelection = () => {
    return (
      <Select
        {...scopeTablePageSizeSelectProps}
        aria-label={t('main.page-size-aria-label')}
        onChange={pageSizeChange}
        options={pageSizeSelectOptions}
        value={currentPageSize}
      ></Select>
    );
  };

  const fetchFilteredDataPage = (
    filters = {},
    pageSize = currentPageSize,
    reportIdOverride?: string
  ) => {
    const requestFilters = resolveFiltersForRequest(filters);
    // Whenever filters change we always logically "go back" to page 1
    setCurrentPage(1);

    if (customPagination) {
      if (onPageChange) {
        onPageChange(1, pageSize, requestFilters);
      } else {
        dispatch(
          getDataPageRequest(
            reportIdOverride || reportId,
            1,
            pageSize,
            transactionType,
            getGroupingType(),
            requestFilters
          )
        );
      }
    }
  };

  const onTableChange = (_paginationConfig, _filters, sorterConfig) => {
    if (!sorterConfig) {
      return;
    }

    const { columnKey, order } = sorterConfig;
    const currentSort = filtersState?.sort?.value as
      | [string, 'asc' | 'desc']
      | undefined;
    const currentKey = currentSort?.[0];
    const currentDir = currentSort?.[1];

    // Prefer the columnKey from AntD; if it's missing (clear state), fall back
    // to whatever column is currently sorted.
    let nextColumnKey = (columnKey as string | undefined) || currentKey;
    let nextOrder: 'ascend' | 'descend' | undefined =
      order && (order === 'ascend' || order === 'descend') ? order : undefined;

    // AntD's third click clears sort (order = undefined). Instead of clearing,
    // treat it as a request to toggle the current direction so the header
    // behaves as a strict 2-state toggle (asc/desc) with no "clear" step.
    if (
      !nextOrder &&
      nextColumnKey &&
      currentKey === nextColumnKey &&
      currentDir
    ) {
      nextOrder = currentDir === 'asc' ? 'descend' : 'ascend';
    }

    // If there was no previous sort direction (first ever click) and AntD gave
    // us no explicit order, default to ascending on the resolved column key.
    if (!nextOrder && nextColumnKey && !currentDir) {
      nextOrder = 'ascend';
    }

    // If we still don't have a valid next order or column, do nothing.
    if (!nextOrder || !nextColumnKey) {
      return;
    }

    const mappedOrder: 'asc' | 'desc' = nextOrder === 'ascend' ? 'asc' : 'desc';

    const filtersStateCopy = resolveFiltersForRequest({ ...filtersState });
    delete filtersStateCopy['sort'];

    filtersStateCopy['sort'] = {
      value: [nextColumnKey, mappedOrder],
    };

    setFiltersState((prev) => ({
      ...prev,
      sort: filtersStateCopy.sort,
    }));

    if (customPagination) {
      if (onPageChange) {
        onPageChange(currentPage, currentPageSize, filtersStateCopy);
      } else {
        dispatch(
          getDataPageRequest(
            reportId,
            currentPage,
            currentPageSize,
            transactionType,
            getGroupingType(),
            filtersStateCopy
          )
        );
      }
    }
  };

  const showColumnsCustomizationButton =
    dataSource?.length > 0 &&
    !!enableColumnsCustomization &&
    !hideToolbarColumnsCustomizationButton;
  const hasToolbarContent =
    !!renderRightComponent ||
    !!renderBelowActionButtons ||
    showColumnsCustomizationButton;

  return (
    <>
      <Row justify="center" style={{}}>
        <Col span={24}>
          {hasToolbarContent ? (
            <div
              style={{
                float: 'inline-end',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: renderBelowActionButtons ? 8 : 0,
                marginBottom: '10px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                {renderRightComponent && (
                  <div
                    style={{
                      minHeight: '32px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {renderRightComponent}
                  </div>
                )}
                {showColumnsCustomizationButton && (
                  <CustomiseColumnsIconButtonWithTooltip
                    type="default"
                    disabled={disableColumnsCustomization}
                    style={customiseColumnsIconButtonStyle()}
                    icon={
                      <SettingOutlined
                        style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
                      />
                    }
                    onClick={() => {
                      const drawerData: DrawerData = {
                        title:
                          columnsCustomizationDrawerTitle ?? t('main.columns'),
                        type: DrawerType.CUSTOMIZE_COLUMNS,
                        component: (
                          <ColumnCustomizationDrawer
                            columns={customizableColumnsForDrawer}
                            visibleColumns={defaultVisibleColumnsForDrawer}
                            lockedColumns={lockedColumns}
                            columnOrder={columnOrder}
                            alwaysFixedColumns={alwaysFixedColumns}
                            optionalColumnKeys={optionalColumnKeys}
                            topSection={columnsCustomizationDrawerTopSection}
                            saveButtonLabel={
                              columnsCustomizationDrawerSaveLabel
                            }
                            onSave={(config) => {
                              if (onColumnsChange) {
                                onColumnsChange(config);
                              }
                            }}
                            onClose={() => dispatch(turnDrawerOff())}
                          />
                        ),
                      };
                      dispatch(turnDrawerOn(drawerData));
                    }}
                  />
                )}
              </div>
              {renderBelowActionButtons}
            </div>
          ) : null}
          <StyledTable
            className={className ? className : 'no-title-padding'}
            dataSource={dataSource}
            columns={filteredColumns}
            rowKey={rowKey ? rowKey : (record) => record.key}
            locale={{
              triggerDesc: t('main.sortDescending'),
              triggerAsc: t('main.sortAscending'),
              // Sorting is now a strict 2-state toggle (asc/desc) with no true
              // "cancel" state, so reuse the generic sortAscending message here
              // to avoid showing misleading "click to cancel" copy.
              cancelSort: t('main.sortAscending'),
            }}
            size={size}
            pagination={
              pagination && !customPagination
                ? {
                    pageSize: rowsPerPageLocal,
                    itemRender: renderPaginationButtons,
                    showTotal: renderPaginationText,
                    showSizeChanger: pageSizeChangerProps,
                    pageSizeOptions: pageSizeSelectOptions.map((option) =>
                      String(option.value)
                    ),
                    onChange(_page, pageSize) {
                      setRowsPerPageLocal(pageSize);
                      getNewPageSize(pageSize);
                    },
                    //position: ['top', 'bottom'],
                  }
                : false
            }
            rowheight={rowHeight}
            fontSize={fontSize}
            headerFontSize={headerFontSize}
            title={titleComponent ? () => titleComponent : undefined}
            showSorterTooltip={{
              styles: {
                body: {
                  fontSize: 13,
                  fontWeight: 400,
                  backgroundColor: 'var(--color-surface-raised, #ffffff)',
                  color: 'var(--color-text-muted, #5a6a6a)',
                },
              },
            }}
            rowSelection={rowSelection}
            scroll={scroll}
            rowClassName={rowClassName}
            onRow={onRow}
            onChange={onTableChange}
            summary={summary}
          />
          {pagination && customPagination && (
            <div
              className={pwaMobileNav ? 'scope-pwa-table-pager' : undefined}
              style={{
                marginTop: 16,
                textAlign: 'end',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                justifyContent: paginationSummaryOverride
                  ? 'flex-start'
                  : 'space-between',
              }}
            >
              <div style={{ flexShrink: 0, minWidth: 'max-content' }}>
                {renderPaginationText(null, [
                  (currentPage - 1) * currentPageSize + 1,
                  Math.min(currentPage * currentPageSize, total || 0),
                ])}
              </div>
              {!paginationSummaryOverride ? (
                <div
                  className={
                    pwaMobileNav ? 'scope-pwa-table-pager__nav' : undefined
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    marginInlineStart: pwaMobileNav ? 0 : 'auto',
                    gap: pwaMobileNav ? 4 : undefined,
                  }}
                >
                  {renderPaginationButtons(0, 'first', null)}
                  {renderPaginationButtons(0, 'prev', null)}
                  <span
                    className={
                      pwaMobileNav ? 'scope-pwa-table-pager__page' : undefined
                    }
                    style={{
                      marginInline: pwaMobileNav ? '0' : '8px',
                      fontSize: pwaMobileNav ? 11 : 12,
                      fontWeight: pwaMobileNav ? 600 : 400,
                      color: 'var(--color-text-base, #042a2a)',
                    }}
                  >
                    {localizeDigitsForLocale(
                      `${formatDisplayInteger(
                        currentPage,
                        i18n.language
                      )}/${formatDisplayInteger(
                        typeof total === 'number'
                          ? Math.max(
                              1,
                              Math.ceil(
                                total /
                                  (currentPageSize > 0 ? currentPageSize : 1)
                              ) || 1
                            )
                          : lastPage,
                        i18n.language
                      )}`,
                      i18n.language
                    )}
                  </span>
                  {renderPaginationButtons(0, 'next', null)}
                  {renderPaginationButtons(0, 'last', null)}
                  {showSizeChanger &&
                    !pwaMobileNav &&
                    renderPageSizeSelection()}
                </div>
              ) : null}
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};
export const ScopeTable = React.memo(ScopeTableInner);
